import React, { useCallback } from 'react';
import { css } from '@emotion/css';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { CheckmarkOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Avatar, BaseMenuItem, LinkButton, List, Popover, Stack, Typography } from '@lyearn/ui';
import stopPropagation from '@/consts/stopPropogation';
import { UserFragment } from '@/graphql/fragments/generated/User';

import { assignButtonStyles, linkButtonStyles } from '../ActionItem/styles';
import AvatarButton from '../AssignMenu/AvatarButton';
import { overlay } from '../UserMenu/styles';

const maxWidth = css`
  max-width: 160px;
`;

interface StaticAssignMenuPopoverProps {
  className?: string;
  isDisabled?: boolean;
  selectedUsers?: UserFragment[];
  participants: UserFragment[];
  onClick: (userId: string) => void;
  children?: React.ReactNode;
  fixedWidth?: boolean;
}

function StaticAssignMenuPopover(props: StaticAssignMenuPopoverProps) {
  const { t } = useTranslation('1on1s');
  const {
    className,
    isDisabled,
    selectedUsers,
    participants,
    onClick,
    children,
    fixedWidth = false,
  } = props;
  const [isPopoverOpen, { toggle }] = useToggle();
  const _onClick = useCallback(
    (userId: string) => {
      onClick(userId);
      toggle();
    },
    [onClick, toggle],
  );

  return (
    <Stack
      alignItems="center"
      className={cn(className, { 'w-[15rem]': fixedWidth })}
      onClick={stopPropagation}>
      <Popover open={isPopoverOpen} onOpenChange={toggle}>
        <Popover.Button disabled={isDisabled}>
          {children || (
            <LinkButton
              className={cn(
                'py-2 px-4',
                overlay,
                className,
                'h-30 flex-1 justify-start hover:bg-bg-hover active:bg-divider',
                linkButtonStyles,
                assignButtonStyles,
              )}>
              <AvatarButton
                showAssignedToTooltip
                showName
                classes={{
                  color: 'text-text-secondary',
                }}
                selectedUsers={selectedUsers}
              />
            </LinkButton>
          )}
        </Popover.Button>
        <Popover.Content minWidth={24} position="bottom-start">
          <List className="py-12 px-8">
            <Typography as="p" className="mb-8 ms-16" variant="label-02">
              {t('1on1s:Editor.AssignTo')}
            </Typography>
            {participants.map((item) => (
              <BaseMenuItem
                key={item.id}
                className={cn(
                  'mt-2 flex h-40 items-center justify-between rounded-8 px-12 hover:bg-interactive-white-hover',
                  { 'bg-bg-secondary': item.id === selectedUsers?.[0]?.id },
                )}
                data-action={item.id}
                tabIndex={0}
                onClick={() => _onClick(item.id)}>
                <Stack alignItems="center" className="w-full" justifyContent="between">
                  <Stack>
                    <Avatar
                      alt={`${item.name} profile picture`}
                      className="me-12"
                      size={24}
                      src={item.profilePicture ?? ''}
                    />
                    <Typography
                      className={cn('truncate', maxWidth)}
                      color={
                        item.id === selectedUsers?.[0]?.id
                          ? 'text-text-primary'
                          : 'text-text-secondary'
                      }
                      variant={'body-short-02'}>
                      {item.name}
                    </Typography>
                  </Stack>
                  {item.id === selectedUsers?.[0]?.id ? (
                    <CheckmarkOutline className="h-20 w-20 flex-shrink-0 ms-4" />
                  ) : null}
                </Stack>
              </BaseMenuItem>
            ))}
          </List>
        </Popover.Content>
      </Popover>
    </Stack>
  );
}

export default StaticAssignMenuPopover;
